import Modal from '@components/v2/ui/Modal/Modal';
import { yupResolver } from '@hookform/resolvers/yup';
import { Copy, Share } from 'lucide-react';
import { useCallback, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import useMockLoading from '../../components/Hooks/useMockLoading';
import config from '../../config';
import LangEnum from '../../lang/LangEnum';
import { useFindSharedEntityByEntity } from '../../services/modules/shared-entities/sharedEntities.queries';
import snackbarStore from '../../stores/Snackbar.store';
import { ShareCatalogTestIds } from '../../utils/testIds/ShareCatalog.testIds';
import { useApplicationContext } from '../context/ApplicationContext/ApplicationContext';
import { useTranslation } from '../translation/TranslationContext';
import ShareCatalogContent from './components/ShareCatalogContent';
import useSharedEntities from './hooks/useSharedEntities';
import { createLink, deleteLinks } from './repository/shared-entities.repository';
import { shareCatalogSchema } from './schemas/shareCatalog.schema';
import { AccessConfig, EntityType, ShareChannelEnum } from './types/sharedEntities.enum';
import {
  IDeleteSharedEntityDto,
  IEntityConfigForm,
  ISharedEntityDto,
  ISharedEntityForm,
  ShareCatalogModalProps,
} from './types/sharedEntities.types';

const ShareCatalogModal = ({
  isOpen,
  catalogId,
  enablePublicSharing,
  catalogName,
  testId,
  onClose,
}: ShareCatalogModalProps) => {
  const {
    canEditCatalogAccess,
    handleCopyLink,
    handleShareLinkByEmail,
    handleShareLinkByWhatsapp,
  } = useSharedEntities({ enablePublicSharing });
  const { translate } = useTranslation();
  const { state, team } = useApplicationContext();
  const [openDeleteLinksModal, setOpenDeleteLinksModal] = useState(false);
  const { data: hasActiveLinks, refetch: refetchActiveSharedEntity } = useFindSharedEntityByEntity(
    { entity: EntityType.Catalog, entityId: catalogId },
    { enabled: canEditCatalogAccess },
  );
  const brand = state?.brand || team?.brand;
  const catalogsPath = `/catalogs/${catalogId}/products`;
  const catalogURL = `${config.webUrl}/${brand?.slug}?redirectPath=${catalogsPath}`;

  const handleOpenDeleteLinksModal = () => {
    setOpenDeleteLinksModal(true);
  };
  const handleCloseDeleteLinksModal = () => {
    setOpenDeleteLinksModal(false);
  };

  const shareCatalogForm = useForm<ISharedEntityForm>({
    values: {
      accessConfig: AccessConfig.Private,
      config: {
        channelType: ShareChannelEnum.WHATSAPP,
        emails: [],
        enableProductDetails: true,
        priceTableId: undefined,
        showAvailableStock: true,
        showSellInPrices: true,
        showSellOutPrices: true,
        isDefaultPriceTableId: true,
        entity: EntityType.Catalog,
      } as IEntityConfigForm,
    },
    resolver: yupResolver(shareCatalogSchema(translate)) as any,
  });

  const handlePrivateShareOrder = useCallback(
    async (values: ISharedEntityForm, emails: string[]) => {
      if (values.config.onlyCopy) {
        handleCopyLink(catalogURL);
        shareCatalogForm.setValue('config.onlyCopy', false);
        return;
      }

      if (values.config.channelType === ShareChannelEnum.WHATSAPP) {
        const whatsappMessage = `${translate(
          LangEnum.SUGGESTED_ORDER_WHATSAPP_SHARE_MESSAGE,
        )} \n ${catalogURL}`;
        handleShareLinkByWhatsapp({ message: whatsappMessage });
        return;
      }

      const globalMessage = `${translate(
        LangEnum.SUGGESTED_ORDER_WHATSAPP_SHARE_MESSAGE,
      )} \n ${catalogURL}`;

      const htmlMessage = globalMessage.replaceAll('\n', '<br />');

      const data = {
        emails,
        emailBody: htmlMessage,
        emailSubject: translate(LangEnum.GLOBAL_SUGGESTED_ORDER, {
          brandName: state.brand?.name,
        }),
      };

      handleShareLinkByEmail(data);
    },
    [
      catalogURL,
      shareCatalogForm,
      state.brand?.name,
      handleCopyLink,
      handleShareLinkByEmail,
      handleShareLinkByWhatsapp,
      translate,
    ],
  );

  const handlePublicShareOrder = useCallback(
    async (values: ISharedEntityForm, emails: string[]) => {
      const payload: ISharedEntityDto = {
        entity: EntityType.Catalog,
        entityId: catalogId,
        config: {
          slug: team?.brand?.slug,
          channelType: values.config.channelType,
          emails: emails || [],
          enableProductDetails: values.config.enableProductDetails,
          showAvailableStock: values.config.showAvailableStock,
          showSellInPrices: values.config.showSellInPrices,
          showSellOutPrices: values.config.showSellOutPrices,
          priceTableId: values.config.priceTableId,
          entity: EntityType.Catalog,
          catalogName: catalogName,
        },
      };
      const response = await createLink(payload);

      if (response.ok) {
        refetchActiveSharedEntity();
        if (values.config.onlyCopy) {
          handleCopyLink(response?.data?.url!);
          shareCatalogForm.setValue('config.onlyCopy', false);
          return;
        }

        if (values.config.channelType === ShareChannelEnum.WHATSAPP) {
          const globalMessage = `${translate(LangEnum.SUGGESTED_ORDER_WHATSAPP_SHARE_MESSAGE)} \n ${
            response.data?.url
          }`;

          handleShareLinkByWhatsapp({ message: globalMessage });
          return;
        }
        snackbarStore.handleMessage({
          type: 'success',
          message: translate(LangEnum.CATALOGS_SHARE_MODAL_SEND_EMAILS_SUCCESS),
        });

        return;
      }

      snackbarStore.handleMessage({
        type: 'error',
        message: response?.data?.url,
      });
    },
    [
      catalogId,
      catalogName,
      handleCopyLink,
      handleShareLinkByWhatsapp,
      refetchActiveSharedEntity,
      shareCatalogForm,
      team?.brand?.slug,
      translate,
    ],
  );

  const handleSubmit = useCallback(
    async (values: ISharedEntityForm) => {
      if (!catalogId) {
        return;
      }
      const isPrivate = values.accessConfig === AccessConfig.Private;
      const emails = values.config.emails.map((option) =>
        option.id === 'new' ? option.newEmail || '' : option.email,
      );

      if (isPrivate) {
        await handlePrivateShareOrder(values, emails);
        return;
      }

      await handlePublicShareOrder(values, emails);
    },
    [catalogId, handlePrivateShareOrder, handlePublicShareOrder],
  );

  const handleDeleteLinks = async () => {
    if (!catalogId) {
      return;
    }
    const payload: IDeleteSharedEntityDto = {
      entity: EntityType.Catalog,
      entityId: catalogId,
    };

    const response = await deleteLinks(payload);
    setOpenDeleteLinksModal(false);
    if (response.ok) {
      refetchActiveSharedEntity();
      snackbarStore.handleMessage({
        type: 'success',
        message: translate(LangEnum.SHARE_ENTITY_DELETE_LINK_SUCCESS),
      });
      return;
    }
    snackbarStore.handleMessage({
      type: 'error',
      message: translate(LangEnum.SHARE_ENTITY_DELETE_LINK_FAILED),
    });
  };

  const { isLoading: catalogSubmitLoading, handleAsyncFunction: handleSubmitCatalog } =
    useMockLoading({
      asyncFunction: handleSubmit,
    });

  const accessConfig = shareCatalogForm.watch('accessConfig');

  return (
    <>
      <Modal
        isOpen={isOpen}
        size="expanded"
        testId={testId}
        header={{ title: translate(LangEnum.CATALOGS_SHARE_MODAL_TITLE), icon: Share }}
        onClose={onClose}
        footer={{
          tertiaryAction:
            accessConfig === AccessConfig.Public && hasActiveLinks
              ? {
                  label: LangEnum.SHARE_ENTITY_CANCEL_LINKS,
                  testId: ShareCatalogTestIds.CANCEL_BUTTON,
                  onClick: handleOpenDeleteLinksModal,
                }
              : undefined,
          secondaryAction: {
            label: LangEnum.GLOBAL_GENERAL_LINK,
            startIcon: Copy,
            testId: ShareCatalogTestIds.GENERATE_LINK_BUTTON,
            onClick: () => {
              shareCatalogForm.setValue('config.onlyCopy', true);
              shareCatalogForm.handleSubmit(handleSubmitCatalog)();
            },
          },
          primaryAction: {
            label: LangEnum.GLOBAL_SHARE,
            isLoading: catalogSubmitLoading,
            testId: ShareCatalogTestIds.SHARE_BUTTON,
            onClick: () => shareCatalogForm.handleSubmit(handleSubmitCatalog)(),
          },
        }}
      >
        <FormProvider {...shareCatalogForm}>
          <ShareCatalogContent entityId={catalogId} disabledConfigAccess={!canEditCatalogAccess} />
        </FormProvider>
      </Modal>

      <Modal
        isOpen={openDeleteLinksModal}
        size="compact"
        header={{ title: LangEnum.SHARE_ENTITY_CANCEL_LINKS_MODAL_TITLE }}
        onClose={handleCloseDeleteLinksModal}
        footer={{
          primaryAction: { label: LangEnum.GLOBAL_YES, onClick: handleDeleteLinks },
          secondaryAction: {
            label: LangEnum.GLOBAL_NO,
            onClick: handleCloseDeleteLinksModal,
          },
        }}
      >
        {translate(LangEnum.SHARE_ENTITY_CANCEL_LINKS_MODAL_SUBTITLE)}
      </Modal>
    </>
  );
};

export default ShareCatalogModal;
